import { catalogItemsForTool, categoryPlanningValue, itemPlanningValue, itemSubcategoryKey } from './billToItemCatalog';
import { extractClosingStockRows } from '../redux/closingStockApi';
import { aggregateWeeklyItemHistory } from './billToDailySales';
import { roundTo, toNumber } from './billToOrderCalc';
import { buildSkuPlanningRows, closingStockRowsFromWizard } from './billToSkuPlanning';

const STORAGE_KEY = 'groversweets_bill_to_flow_tools';
const DRAFT_STEP_KEY = 'groversweets_my_tool_draft_step';

export function saveDraftWizardStep(toolId, step) {
  const id = String(toolId || '').trim();
  const n = Number(step);
  if (!id || !Number.isFinite(n) || n < 0 || n > 3) return;
  try {
    localStorage.setItem(DRAFT_STEP_KEY, JSON.stringify({ id, step: n }));
  } catch {
    // ignore storage failures
  }
}

export function loadDraftWizardStep(toolId) {
  const id = String(toolId || '').trim();
  if (!id) return null;
  try {
    const parsed = JSON.parse(localStorage.getItem(DRAFT_STEP_KEY) || 'null');
    if (!parsed || String(parsed.id) !== id) return null;
    const n = Number(parsed.step);
    if (!Number.isFinite(n) || n < 0 || n > 3) return null;
    return n;
  } catch {
    return null;
  }
}

export function clearDraftWizardStep(toolId) {
  const id = String(toolId || '').trim();
  try {
    const parsed = JSON.parse(localStorage.getItem(DRAFT_STEP_KEY) || 'null');
    if (!id || !parsed || String(parsed.id) === id) {
      localStorage.removeItem(DRAFT_STEP_KEY);
    }
  } catch {
    localStorage.removeItem(DRAFT_STEP_KEY);
  }
}

export const TOOL_TYPE_LABELS = {
  showroom_item: 'Showroom',
  sweet_category: 'Showroom',
  restaurant_item: 'Restaurant',
  rm_pm: 'Mart',
};

export const TOOL_TYPE_PRODUCT_TYPE = {
  showroom_item: 'Other',
  sweet_category: 'Other',
  restaurant_item: 'Restaurant',
  rm_pm: 'Mart',
};

export function toolTypeLabel(tool) {
  const fromApi = String(tool?.productTypeName || tool?.toolType || '').trim();
  if (fromApi && !TOOL_TYPE_LABELS[fromApi]) return fromApi;
  return (
    tool?.productTypeName ||
    TOOL_TYPE_LABELS[tool?.toolType] ||
    tool?.toolType ||
    '—'
  );
}

export function myToolProductCount(tool) {
  if (!tool) return 0;
  if (Array.isArray(tool.products) && tool.products.length) return tool.products.length;
  if (Array.isArray(tool.productIds) && tool.productIds.length) return tool.productIds.length;
  return 0;
}

export function unwrapMyTool(payload) {
  if (!payload || typeof payload !== 'object') return null;
  const candidates = [
    payload.my_tool,
    payload.data?.my_tool,
    payload.data?.data?.my_tool,
    payload.tool,
    payload.data?.tool,
    payload.data?.data,
    payload.data,
  ];
  for (const candidate of candidates) {
    if (
      candidate &&
      typeof candidate === 'object' &&
      !Array.isArray(candidate) &&
      (candidate.id != null ||
        candidate.tool_name ||
        candidate.toolName ||
        candidate.my_tool_projects ||
        candidate.projects)
    ) {
      return candidate;
    }
  }
  if (Array.isArray(payload.data) && payload.data[0] && typeof payload.data[0] === 'object') {
    return payload.data[0];
  }
  if (payload.id != null || payload.tool_name || payload.toolName) return payload;
  return null;
}

function asRecordArray(value) {
  if (Array.isArray(value)) return value.filter((row) => row && typeof row === 'object');
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    if (
      value.product_id != null ||
      value.productId != null ||
      value.product ||
      value.build_to != null ||
      value.total_need != null ||
      value.final_order != null
    ) {
      return [value];
    }
  }
  return [];
}

function unwrapProjectRow(item) {
  if (!item || typeof item !== 'object') return item;
  return asObject(item.my_tool_project) || asObject(item.project) || item;
}

function collectMyToolProjects(row) {
  if (!row || typeof row !== 'object') return [];

  const directKeys = [
    'my_tool_projects',
    'my_tool_projects_attributes',
    'myToolProjects',
    'my_tool_project',
    'projects',
    'products',
  ];

  function fromNode(node) {
    if (!node || typeof node !== 'object') return [];
    for (const key of directKeys) {
      const list = asRecordArray(node[key]).map(unwrapProjectRow);
      if (list.length) return list;
    }
    const categories = asRecordArray(
      node.my_tool_project_categories || node.my_tool_project_category
    );
    if (categories.length) {
      const nested = categories.flatMap((category) => fromNode(category));
      if (nested.length) return nested;
    }
    return [];
  }

  const fromRoot = fromNode(row);
  if (fromRoot.length) return fromRoot;
  const fromData = fromNode(row.data);
  if (fromData.length) return fromData;
  return fromNode(row.my_tool);
}

function pickFrom(objects, ...keys) {
  for (const obj of objects) {
    const value = pick(obj, ...keys);
    if (value !== '') return value;
  }
  return '';
}

export function mapMyToolProduct(row = {}) {
  const product = asObject(row.product) || {};
  const categoryWrap =
    asObject(row.category) ||
    asObject(row.my_tool_project_category) ||
    asObject(row.project_category) ||
    asObject(row.product_category) ||
    {};
  const nestedCategory =
    asObject(categoryWrap.product_category) ||
    asObject(row.product_category) ||
    asObject(product.product_category) ||
    {};
  const subcategoryWrap =
    asObject(row.subcategory) ||
    asObject(row.sub_category) ||
    asObject(row.product_subcategory) ||
    asObject(row.my_tool_project_subcategory) ||
    asObject(product.product_subcategory) ||
    {};
  const nestedSubcategory =
    asObject(subcategoryWrap.product_subcategory) ||
    asObject(product.product_subcategory) ||
    {};

  return {
    id: row.id ?? row.product_id ?? product.id ?? '',
    productId: row.product_id ?? product.id ?? '',
    itemCode: String(
      pickFrom([row, product], 'item_code', 'itemCode', 'code') || ''
    ).trim(),
    itemName: String(
      pickFrom([row, product], 'product_name', 'item_name', 'itemName', 'name') || ''
    ).trim(),
    category: String(
      pickFrom([categoryWrap, nestedCategory, row], 'product_category_name', 'category_name') ||
        nestedName(nestedCategory) ||
        nestedName(categoryWrap) ||
        ''
    ).trim(),
    subCategory: String(
      pickFrom(
        [subcategoryWrap, nestedSubcategory, row, product, categoryWrap],
        'product_subcategory_name',
        'subcategory_name',
        'sub_category_name',
        'subCategory'
      ) ||
        nestedName(nestedSubcategory) ||
        nestedName(subcategoryWrap) ||
        ''
    ).trim(),
    leadTime: pickFrom([row, product], 'lead_time', 'leadTime'),
    buffer: pickFrom([row, product], 'buffer', 'buffer_percent', 'bufferPercent'),
    growth: pickFrom([row, product], 'growth', 'project_growth', 'projectGrowth', 'guest_count', 'guestCount'),
    projectSale: pickFrom([row], 'project_sale', 'projectSale', 'projected_sale', 'projectedSale'),
    projectGc: pickFrom([row], 'project_gc', 'projectGc', 'projected_gc', 'projectedGc'),
    sps1: pickFrom([product, row], 'sps_1', 'sps1'),
    sps2: pickFrom([product, row], 'sps_2', 'sps2'),
    sps3: pickFrom([product, row], 'sps_3', 'sps3'),
    buildTo: pickFrom([row], 'total_need', 'totalNeed', 'build_to', 'buildTo'),
    closingStock: pickFrom([row], 'closing_stock', 'closingStock'),
    suggestedOrder: pickFrom(
      [row],
      'total_requirement',
      'suggested_order',
      'suggestedOrder',
      'totalRequirement'
    ),
    finalOrder: pickFrom([row], 'final_order', 'finalOrder'),
    finalOrderByOutlet: pickFrom([row], 'final_order_by_outlet', 'finalOrderByOutlet'),
    spsOrd1: pickFrom([row], 'sps_order_1', 'spsOrder1', 'sps_ord_1'),
    spsOrd2: pickFrom([row], 'sps_order_2', 'spsOrder2', 'sps_ord_2'),
    spsOrd3: pickFrom([row], 'sps_order_3', 'spsOrder3', 'sps_ord_3'),
    projectId:
      row.my_tool_project_id ??
      (row.product_id != null && row.id != null && String(row.id) !== String(row.product_id)
        ? row.id
        : ''),
    categoryId: String(
      pickFrom(
        [categoryWrap, nestedCategory, row, product],
        'product_category_id',
        'productCategoryId',
        'category_id'
      ) ||
        nestedCategory.id ||
        ''
    ).trim(),
    subcategoryId: String(
      pickFrom(
        [subcategoryWrap, nestedSubcategory, row, product],
        'product_subcategory_id',
        'productSubcategoryId',
        'subcategory_id',
        'subcategoryId'
      ) ||
        nestedSubcategory.id ||
        ''
    ).trim(),
    categoryLeadTime: pickFrom([categoryWrap], 'category_lead_time', 'lead_time', 'leadTime'),
    categoryBuffer: pickFrom([categoryWrap], 'category_buffer', 'buffer', 'buffer_percent'),
    categoryGrowth: pickFrom([categoryWrap], 'category_growth', 'growth', 'guest_count'),
  };
}

export function subcategoryNameFromProduct(row = {}) {
  const nested =
    asObject(row.product_subcategory) ||
    asObject(row.productSubcategory) ||
    asObject(row.subcategory) ||
    asObject(row.sub_category) ||
    {};
  return String(
    pickFrom(
      [row, nested],
      'product_subcategory_name',
      'productSubcategoryName',
      'subcategory_name',
      'subCategoryName',
      'sub_category_name'
    ) ||
      nestedName(nested) ||
      ''
  ).trim();
}

export function mergeMyToolDetails(apiTool, previewTool) {
  if (!apiTool && !previewTool) return null;
  if (!apiTool) return previewTool;
  if (!previewTool) return apiTool;
  const apiProducts = Array.isArray(apiTool.products) ? apiTool.products : [];
  const previewProducts = Array.isArray(previewTool.products) ? previewTool.products : [];
  return {
    ...previewTool,
    ...apiTool,
    toolName: apiTool.toolName || previewTool.toolName,
    toolCode: apiTool.toolCode || previewTool.toolCode,
    description: apiTool.description || previewTool.description,
    locationName: apiTool.locationName || previewTool.locationName,
    totalProjectSale: apiTool.totalProjectSale || previewTool.totalProjectSale,
    totalProjectGc: apiTool.totalProjectGc || previewTool.totalProjectGc,
    products: apiProducts.length ? apiProducts : previewProducts,
    productIds:
      Array.isArray(apiTool.productIds) && apiTool.productIds.length
        ? apiTool.productIds
        : previewTool.productIds,
  };
}

export function loadMyTools() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

export function saveMyTool(tool) {
  const list = loadMyTools();
  const next = [tool, ...list.filter((item) => String(item?.id) !== String(tool?.id))];
  localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  return next;
}

function asProjectAttributeId(value) {
  if (value == null || String(value).trim() === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : value;
}

function asApiNumber(value, digits = 1) {
  const n = toNumber(value, NaN);
  if (!Number.isFinite(n)) return 0;
  return roundTo(n, digits);
}

/** PUT /api/my_tools/:id body from wizard state. */
export function buildCreateMyToolPayload(wizardData, { status = 'draft' } = {}) {
  const items = catalogItemsForTool(wizardData);
  const weekCount = Math.max(3, toNumber(wizardData?.parameters?.salesHistoryWeeks, 3));
  const weeklyHistory = aggregateWeeklyItemHistory(wizardData?.dailySalesRows || [], weekCount);
  const params = wizardData?.parameters || {};
  const location_id = Number(wizardData?.outletId);
  const toolName =
    String(wizardData?.toolName || '').trim() ||
    String(wizardData?.outletName || '').trim() ||
    String(wizardData?.productTypeName || wizardData?.toolType || '').trim() ||
    'Draft Tool';
  const toolCode = String(wizardData?.toolCode || '').trim();
  const projectIdsByProduct = wizardData?.myToolProjectIds || {};
  const closingStockRows = closingStockRowsFromWizard(wizardData);
  const planningRows = buildSkuPlanningRows({
    items,
    wizardData,
    weeklyHistory,
    weekCount,
    closingStockRows,
  });

  const my_tool_projects_attributes = planningRows
    .map((row) => {
      const item = row.item;
      const productId = Number(item.productId || item.id);
      const categoryId = Number(item.sheet);
      if (!Number.isFinite(productId)) return null;
      const outletOrder =
        row.finalOrderByOutlet === '' || row.finalOrderByOutlet == null
          ? row.finalOrder
          : row.finalOrderByOutlet;
      const projectRowId =
        projectIdsByProduct[String(productId)] ??
        projectIdsByProduct[productId] ??
        item.projectId;
      const projectId = asProjectAttributeId(projectRowId);
      return {
        ...(projectId != null ? { id: projectId } : {}),
        product_id: productId,
        project_sale: row.projectedSales == null ? 0 : roundTo(row.projectedSales, 0),
        project_gc: row.projectedGc == null ? 0 : roundTo(row.projectedGc, 0),
        buffer: asApiNumber(itemPlanningValue(item, wizardData, 'bufferPercent'), 1),
        lead_time: asApiNumber(itemPlanningValue(item, wizardData, 'leadTime'), 0),
        growth: asApiNumber(itemPlanningValue(item, wizardData, 'projectedGrowth'), 1),
        total_need: asApiNumber(row.buildTo, 1),
        sps_order_1: asApiNumber(row.spsOrd1, 0),
        sps_order_2: asApiNumber(row.spsOrd2, 0),
        sps_order_3: asApiNumber(row.spsOrd3, 0),
        final_order: asApiNumber(row.finalOrder, 0),
        closing_stock: asApiNumber(row.closingStock, 0),
        total_requirement: asApiNumber(row.suggestedOrder, 1),
        final_order_by_outlet: asApiNumber(outletOrder, 0),
        my_tool_project_category_attributes: {
          product_category_id: Number.isFinite(categoryId) ? categoryId : item.sheet,
          category_buffer: asApiNumber(
            categoryPlanningValue(wizardData, item.sheet, 'bufferPercent'),
            1
          ),
          category_lead_time: asApiNumber(
            categoryPlanningValue(wizardData, item.sheet, 'leadTime'),
            0
          ),
          category_growth: asApiNumber(
            categoryPlanningValue(wizardData, item.sheet, 'projectedGrowth'),
            1
          ),
        },
      };
    })
    .filter(Boolean);

  return {
    my_tool: {
      tool_name: toolName,
      ...(toolCode ? { tool_code: toolCode } : {}),
      tool_type: String(wizardData?.productTypeName || wizardData?.toolType || '').trim(),
      date: String(wizardData?.toolDate || '').trim(),
      location_id: Number.isFinite(location_id) ? location_id : wizardData?.outletId,
      description: String(wizardData?.toolDescription || '').trim(),
      total_project_sale: String(params.projectedSales ?? '').trim() || '0',
      total_project_gc: String(params.projectedGc ?? '').trim() || '0',
      status,
      my_tool_projects_attributes,
    },
  };
}

export function myToolProjectIdsFromResponse(res) {
  const ids = {};
  const unwrapped = unwrapMyTool(res);
  const projects = collectMyToolProjects(unwrapped);
  projects.forEach((row) => {
    if (!row || typeof row !== 'object') return;
    const product = asObject(row.product) || {};
    const productId = String(row.product_id ?? row.productId ?? product.id ?? '').trim();
    const projectId = row.my_tool_project_id ?? row.id;
    if (!productId || projectId == null || String(projectId).trim() === '') return;
    if (String(projectId) === productId) return;
    const n = Number(projectId);
    ids[productId] = Number.isFinite(n) ? n : projectId;
  });
  return ids;
}

export function mergeMyToolProjectIds(current, incoming) {
  return { ...(current || {}), ...(incoming || {}) };
}

function meaningfulPlanningNumber(value) {
  if (value == null || value === '') return '';
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return '';
  return String(value);
}

function asStoredMetric(value) {
  if (value == null || value === '') return '';
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return '';
  return String(value);
}

/** Map import/show `projects` onto wizard closing stock + planning fields. */
export function wizardStateFromMyToolResponse(payload, wizardData = {}) {
  const closingRows = extractClosingStockRows(payload);
  const projectIds = myToolProjectIdsFromResponse(payload);
  const projects = collectMyToolProjects(unwrapMyTool(payload));
  const items = catalogItemsForTool(wizardData);
  const planningBySku = { ...(wizardData.planningBySku || {}) };
  const planningBySubcategory = { ...(wizardData.planningBySubcategory || {}) };

  projects.forEach((project) => {
    if (!project || typeof project !== 'object') return;
    const product = asObject(project.product) || {};
    const productId = String(
      project.product_id ?? project.productId ?? product.id ?? ''
    ).trim();
    if (!productId) return;
    const item = items.find((row) => String(row.productId || row.id) === productId);
    if (!item?.selectionKey) return;
    const mappedProduct = mapMyToolProduct(project);
    const rec = { ...(planningBySku[item.selectionKey] || {}) };
    const buffer = meaningfulPlanningNumber(mappedProduct.buffer ?? project.buffer ?? project.buffer_percent);
    const growth = meaningfulPlanningNumber(
      mappedProduct.growth ?? project.growth ?? project.project_growth ?? project.guest_count
    );
    if (buffer) rec.bufferPercent = buffer;
    if (growth) rec.projectedGrowth = growth;
    const projectedSales = asStoredMetric(mappedProduct.projectSale);
    const projectedGc = asStoredMetric(mappedProduct.projectGc);
    const buildTo = asStoredMetric(mappedProduct.buildTo);
    const suggestedOrder = asStoredMetric(mappedProduct.suggestedOrder);
    const finalOrder = asStoredMetric(mappedProduct.finalOrder);
    const outletOrder = asStoredMetric(mappedProduct.finalOrderByOutlet);
    const spsOrd1 = asStoredMetric(mappedProduct.spsOrd1);
    const spsOrd2 = asStoredMetric(mappedProduct.spsOrd2);
    const spsOrd3 = asStoredMetric(mappedProduct.spsOrd3);
    if (projectedSales !== '') rec.projectedSales = projectedSales;
    if (projectedGc !== '') rec.projectedGc = projectedGc;
    if (buildTo !== '') rec.buildTo = buildTo;
    if (suggestedOrder !== '') rec.suggestedOrder = suggestedOrder;
    if (finalOrder !== '') rec.finalOrder = finalOrder;
    if (outletOrder !== '' && Number(outletOrder) !== 0) {
      rec.finalOrderByOutlet = outletOrder;
      rec.finalOrderByOutletEdited = true;
    }
    if (spsOrd1 !== '') rec.spsOrd1 = spsOrd1;
    if (spsOrd2 !== '') rec.spsOrd2 = spsOrd2;
    if (spsOrd3 !== '') rec.spsOrd3 = spsOrd3;
    planningBySku[item.selectionKey] = rec;
    const lead = meaningfulPlanningNumber(project.lead_time ?? project.leadTime);
    const subKey = itemSubcategoryKey(item);
    if (lead && subKey) {
      planningBySubcategory[subKey] = {
        ...(planningBySubcategory[subKey] || {}),
        leadTime: lead,
      };
    }
  });

  const nextDraft = wizardData.closingStockDraft && typeof wizardData.closingStockDraft === 'object'
    ? wizardData.closingStockDraft
    : {};
  return {
    myToolProjectIds: mergeMyToolProjectIds(wizardData.myToolProjectIds, projectIds),
    closingStockDraft: closingRows.length ? { ...nextDraft, rows: closingRows } : wizardData.closingStockDraft,
    planningBySku,
    planningBySubcategory,
  };
}

function uniqueStrings(values) {
  const seen = new Set();
  const out = [];
  (values || []).forEach((value) => {
    const key = String(value ?? '').trim();
    if (!key || seen.has(key)) return;
    seen.add(key);
    out.push(key);
  });
  return out;
}

function toDateInputValue(value) {
  if (value == null || value === '') return '';
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw.slice(0, 10);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function parseSalesHistoryWeeks(value) {
  if (value == null || value === '') return '';
  const match = String(value).match(/(\d+)/);
  return match ? match[1] : '';
}

function filledAmount(value) {
  if (value == null || value === '') return '';
  const n = Number(value);
  if (Number.isFinite(n) && n === 0) return '';
  return String(value);
}

export function isIncompleteMyToolDraft(payload) {
  if (payload == null) return false;
  const unwrapped = unwrapMyTool(payload);
  const mapped = unwrapped ? mapMyToolRow(unwrapped) : myToolRowFromCreateResponse(payload);
  const id = mapped?.id;
  if (id == null || String(id).trim() === '') return false;
  const status = String(mapped?.status ?? unwrapped?.status ?? '').toLowerCase().trim();
  return status !== 'completed' && status !== 'complete';
}

function catalogItemFromDraftProduct(product) {
  const categoryId = String(product?.categoryId || '');
  const itemCode = String(product?.itemCode || product?.productId || product?.id || '').trim();
  const productId = String(product?.productId || product?.id || '').trim();
  const subcategoryId = String(product?.subcategoryId || '').trim();
  return {
    id: productId || itemCode,
    productId,
    sheet: categoryId,
    category: product?.category || '',
    subCategory: product?.subCategory || '',
    subcategoryId,
    subcategoryKey: categoryId ? `${categoryId}::${subcategoryId}` : '',
    itemCode,
    itemName: product?.itemName || '',
    sps1: product?.sps1 || '',
    sps2: product?.sps2 || '',
    sps3: product?.sps3 || '',
    selectionKey: `${categoryId}::${itemCode || productId}`,
    bufferPercent: product?.buffer || '',
    projectedGrowth: product?.growth || '',
    projectId: product?.projectId || '',
    projectSale: product?.projectSale ?? '',
    projectGc: product?.projectGc ?? '',
    projectedSales: product?.projectSale ?? '',
    projectedGc: product?.projectGc ?? '',
    buildTo: product?.buildTo ?? '',
    suggestedOrder: product?.suggestedOrder ?? '',
    finalOrder: product?.finalOrder ?? '',
    finalOrderByOutlet: product?.finalOrderByOutlet ?? '',
    spsOrd1: product?.spsOrd1 ?? '',
    spsOrd2: product?.spsOrd2 ?? '',
    spsOrd3: product?.spsOrd3 ?? '',
  };
}

/** Restore wizard fields from GET /api/my_tools/draft (or show) so the user can continue. */
export function hydrateWizardFromMyTool(payload, base = {}) {
  const unwrapped = unwrapMyTool(payload);
  const mapped = unwrapped ? mapMyToolRow(unwrapped) : myToolRowFromCreateResponse(payload);
  const id = mapped?.id != null ? String(mapped.id).trim() : '';
  if (!id) return null;

  const products = Array.isArray(mapped.products) ? mapped.products : [];
  const productCatalog = products.map(catalogItemFromDraftProduct).filter((item) => item.productId || item.itemCode);
  const categories = uniqueStrings(productCatalog.map((item) => item.sheet));
  const subCategories = uniqueStrings(
    productCatalog
      .filter((item) => item.sheet && item.subcategoryId)
      .map((item) => `${item.sheet}::${item.subcategoryId}`)
  );
  const selectedItemKeys = productCatalog.map((item) => item.selectionKey).filter(Boolean);
  const categoryLabels = {};
  const subCategoryLabels = {};
  productCatalog.forEach((item) => {
    if (item.sheet && item.category) categoryLabels[item.sheet] = item.category;
    if (item.subcategoryKey && item.subCategory) {
      subCategoryLabels[item.subcategoryKey] = item.category
        ? `${item.category} · ${item.subCategory}`
        : item.subCategory;
    }
  });

  const productTypeId = String(mapped.productTypeId || '').trim();
  const productTypeName = String(mapped.productTypeName || mapped.toolType || '').trim();
  const toolDate = toDateInputValue(mapped.toolDate);
  const salesHistoryWeeks = parseSalesHistoryWeeks(mapped.salesHistoryWeeks) || (id ? '3' : '');
  const seed = {
    ...base,
    id,
    toolType: productTypeId || productTypeName,
    productTypeId: productTypeId || '',
    productTypeName,
    toolName: mapped.toolName || '',
    toolCode: mapped.toolCode || '',
    toolDescription: mapped.description || '',
    toolDate,
    outletId: mapped.locationId != null ? String(mapped.locationId) : '',
    outletName: mapped.locationName || '',
    toolStatus: mapped.status || 'draft',
    createdBy: mapped.createdBy || base.createdBy || '',
    categories,
    subCategories,
    selectedItemKeys: selectedItemKeys.length ? selectedItemKeys : null,
    productCatalog,
    categoryLabels: { ...(base.categoryLabels || {}), ...categoryLabels },
    subCategoryLabels: { ...(base.subCategoryLabels || {}), ...subCategoryLabels },
    categoriesDefaulted: true,
    parameters: {
      ...(base.parameters || {}),
      salesHistoryWeeks,
      projectedSales: filledAmount(mapped.totalProjectSale),
      projectedGc: filledAmount(mapped.totalProjectGc),
    },
  };

  const planningPatch = wizardStateFromMyToolResponse(payload, seed);
  const closing = planningPatch.closingStockDraft;
  const closingStockDraft =
    closing && typeof closing === 'object'
      ? {
          ...closing,
          locationId: String(mapped.locationId || closing.locationId || ''),
          date: toolDate || closing.date || '',
        }
      : {
          locationId: String(mapped.locationId || ''),
          date: toolDate,
          rows: [],
        };

  return {
    ...seed,
    ...planningPatch,
    closingStockDraft,
  };
}

export function myToolIdFromResponse(res) {
  const mapped = myToolRowFromCreateResponse(res);
  if (mapped?.id != null && String(mapped.id).trim()) return String(mapped.id).trim();
  const raw =
    res?.my_tool?.id ??
    res?.data?.my_tool?.id ??
    res?.data?.id ??
    res?.my_tool_id ??
    res?.data?.my_tool_id ??
    res?.id;
  return raw != null && String(raw).trim() ? String(raw).trim() : '';
}

export function myToolRowFromCreateResponse(res) {
  if (!res || typeof res !== 'object') return null;
  const row =
    res.my_tool ||
    res.data?.my_tool ||
    (res.data && !Array.isArray(res.data) ? res.data : null) ||
    listMyToolsFromResponse(res)[0];
  return row && typeof row === 'object' ? mapMyToolRow(row) : null;
}

export function formatToolCreatedAt(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

export function formatToolDate(value) {
  if (!value) return '—';
  const raw = String(value).trim();
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function pick(row, ...keys) {
  if (!row || typeof row !== 'object') return '';
  for (const key of keys) {
    const value = row[key];
    if (value == null || value === '') continue;
    if (typeof value === 'object') continue;
    return value;
  }
  return '';
}

function asObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : null;
}

function nestedName(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'string' || typeof value === 'number') return String(value).trim();
  if (typeof value === 'object') {
    return String(
      value.product_category_name ||
        value.product_subcategory_name ||
        value.name ||
        value.full_name ||
        value.employee_name ||
        value.account_name ||
        value.user_name ||
        ''
    ).trim();
  }
  return '';
}

export function listMyToolsFromResponse(res) {
  if (Array.isArray(res)) return res;
  const nested = res?.data ?? res ?? [];
  if (Array.isArray(nested)) return nested;
  if (Array.isArray(nested?.data)) return nested.data;
  if (Array.isArray(nested?.my_tools)) return nested.my_tools;
  if (Array.isArray(res?.my_tools)) return res.my_tools;
  if (Array.isArray(nested?.tools)) return nested.tools;
  if (Array.isArray(res?.data?.my_tools)) return res.data.my_tools;
  return [];
}

export function mapMyToolRow(row) {
  if (!row || typeof row !== 'object') return null;
  const productType =
    row.product_type && typeof row.product_type === 'object' ? row.product_type : {};
  const toolTypeLabelFromApi = String(pick(row, 'tool_type', 'toolType') || '').trim();
  const createdBy =
    pick(row, 'account_name', 'created_by_name', 'createdBy') ||
    nestedName(row.created_by) ||
    nestedName(row.account) ||
    nestedName(row.creator) ||
    nestedName(row.created_by_account) ||
    '';
  const products = collectMyToolProjects(row).map(mapMyToolProduct);
  const productIds = Array.isArray(row.product_ids)
    ? row.product_ids
    : Array.isArray(row.productIds)
      ? row.productIds
      : products.map((item) => item.productId).filter((id) => id !== '' && id != null);

  return {
    id: row.id ?? row.tool_id ?? '',
    toolName: String(pick(row, 'tool_name', 'toolName', 'name') || '').trim(),
    toolCode: String(pick(row, 'tool_code', 'toolCode', 'code') || '').trim(),
    toolType: toolTypeLabelFromApi,
    productTypeId: String(
      pick(row, 'product_type_id', 'productTypeId') || productType.id || ''
    ).trim(),
    productTypeName: String(
      toolTypeLabelFromApi ||
        pick(row, 'product_type_name', 'productTypeName', 'tool_type_name') ||
        nestedName(productType) ||
        ''
    ).trim(),
    salesHistoryWeeks: pick(row, 'sales_history_weeks', 'salesHistoryWeeks', 'history_weeks'),
    toolDate: pick(row, 'tool_date', 'toolDate', 'date') || '',
    locationId: pick(row, 'location_id', 'locationId') || '',
    locationName: String(
      pick(row, 'location_name', 'locationName', 'outlet_name') ||
        nestedName(row.location) ||
        nestedName(row.outlet) ||
        ''
    ).trim(),
    description: String(pick(row, 'description', 'toolDescription') || '').trim(),
    totalProjectSale: pick(row, 'total_project_sale', 'totalProjectSale', 'projected_sales', 'projectedSales'),
    totalProjectGc: pick(row, 'total_project_gc', 'totalProjectGc', 'projected_gc', 'projectedGc'),
    productIds,
    products,
    status: row.status,
    createdAt: pick(row, 'created_at', 'createdAt') || '',
    updatedAt: pick(row, 'updated_at', 'updatedAt') || '',
    createdBy: String(createdBy || '').trim(),
  };
}
